import React from 'react'
import type { GradingScheme } from '@/types/grading'

interface GradingBreakdownProps {
  scheme: GradingScheme
  scores: Record<string, number>
}

export const GradingBreakdown: React.FC<GradingBreakdownProps> = ({ scheme, scores }) => {
  const capitalizeType = (type: string): string => {
    return type.charAt(0).toUpperCase() + type.slice(1)
  }

  const calculateWeightedScore = (score: number | undefined, weight: number): number => {
    if (score === undefined) {
      return 0
    }
    return (score * weight) / 100
  }

  const totalWeightedScore = scheme.components.reduce((total, component) => {
    const score = scores[component.type]
    return total + calculateWeightedScore(score, component.weightPercentage)
  }, 0)

  return (
    <div className="grading-breakdown bg-surface-card rounded-xl border border-white/5 p-6">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Grading Breakdown</h3>

      <div className="space-y-3">
        {scheme.components.map((component, index) => {
          const score = scores[component.type]
          const weightedScore = calculateWeightedScore(score, component.weightPercentage)

          return (
            <div
              key={index}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-surface-dark rounded-lg border border-gray-100 dark:border-white/5"
            >
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 dark:text-white">{capitalizeType(component.type)}</h4>
                <p className="text-sm text-gray-600 dark:text-text-muted">Weight: {component.weightPercentage}%</p>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600 dark:text-text-muted">Score</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {score !== undefined ? score : 'N/A'}
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-sm text-gray-600 dark:text-text-muted">Contribution</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {score !== undefined ? weightedScore.toFixed(2) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-6 pt-6 border-t-2 border-gray-200 dark:border-white/10">
        <div className="flex justify-between items-center">
          <h4 className="text-lg font-bold text-gray-900 dark:text-white">Total Weighted Score</h4>
          <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{totalWeightedScore.toFixed(2)}</p>
        </div>
      </div>
    </div>
  )
}
